import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Mail, MessageCircle, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import LanguageToggle from './i18n/LanguageToggle';
import CurrencySelector from './i18n/CurrencySelector';

const NavigationFinal = () => {
  const { user, logout } = useAuth();
  const t = useT();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const mainLinks = [
    { to: '/', testid: 'nav-home', label: { fr: 'Accueil', en: 'Home' } },
    { to: '/about', testid: 'nav-about', label: { fr: 'À propos', en: 'About Us' } },
    { to: '/speaking-lab', testid: 'nav-speaking-lab', label: { fr: 'Speaking Lab', en: 'Speaking Lab' } },
    { to: '/lire-le-francais', testid: 'nav-literature', label: 'Lire le Français' },
    { to: '/les-mots-en-voyage', testid: 'nav-les-mots-en-voyage', label: 'Les Mots en Voyage' },
    { to: '/teachers-portal', testid: 'nav-book-a-teacher', label: { fr: 'Réserver un professeur', en: 'Book a Teacher' } },
    { to: '/pricing', testid: 'nav-pricing', label: { fr: 'Tarifs', en: 'Pricing' } },
  ];

  return (
    <>
      {/* Thin Utility Bar - desktop only */}
      <div className="hidden lg:block bg-[#1A2F4A] text-white py-2 relative z-[55]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-end gap-4 lg:gap-6">
            <LanguageToggle />
            <CurrencySelector />
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-1.5" data-testid="nav-mon-espace">
                  <User className="w-3.5 h-3.5" />
                  {t({ fr: 'Espace étudiant', en: 'Student Portal' })}
                </Link>
                <button onClick={logout} className="text-sm font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-1.5" data-testid="nav-logout">
                  <LogOut className="w-3.5 h-3.5" />
                  {t({ fr: 'Se déconnecter', en: 'Log out' })}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-1.5" data-testid="nav-login">
                  <User className="w-3.5 h-3.5" />
                  {t({ fr: 'Se connecter', en: 'Sign In' })}
                </Link>
                <Link to="/signup" className="text-sm font-medium bg-[#D4AF37] text-[#1A2F4A] px-3 py-0.5 rounded-full hover:bg-[#C9A961] transition-colors" data-testid="nav-signup">
                  {t({ fr: 'Inscription gratuite', en: 'Start for Free' })}
                </Link>
              </>
            )}
            <Link to="/contact" className="text-sm font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-1.5" data-testid="nav-contact">
              <Mail className="w-3.5 h-3.5" />
              {t({ fr: 'Nous contacter', en: 'Contact Us' })}
            </Link>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <nav className="bg-[#FAF7F0] border-b-2 border-[#D4AF37]/30 shadow-sm relative z-40">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="nav-logo">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full overflow-hidden bg-white border-2 sm:border-[3px] border-[#D4AF37] shadow-lg flex items-center justify-center flex-shrink-0">
                <img
                  src="https://customer-assets.emergentagent.com/job_parlezmoifrench/artifacts/25ispvfj_selora%20logo-removebg.jpg"
                  alt="Selora Logo"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover scale-125"
                />
              </div>
              <div className="min-w-0">
                <span className="text-lg sm:text-2xl font-bold text-[#1A2F4A] block leading-tight truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                  SELORA
                </span>
                <p className="text-[9px] sm:text-xs text-[#6B4E37] tracking-widest uppercase truncate">French Academy</p>
              </div>
            </Link>

            {/* Desktop Main Menu */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-10">
              {mainLinks.map((l) => (
                <Link
                  key={l.testid}
                  to={l.to}
                  className="text-[#1A2F4A] font-semibold text-[14px] xl:text-[15px] hover:text-[#D4AF37] transition-colors py-2 whitespace-nowrap"
                  data-testid={l.testid}>
                  {typeof l.label === 'string' ? l.label : t(l.label)}
                </Link>
              ))}
            </div>

            {/* Desktop Right CTA */}
            <Link
              to="/club-francophone"
              className="hidden lg:inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C9A961] text-[#1A2F4A] px-4 xl:px-6 py-2.5 rounded-full text-[13px] xl:text-sm font-bold shadow-md hover:shadow-lg transition-all whitespace-nowrap"
              data-testid="nav-club-francophone">
              <MessageCircle className="w-4 h-4" />
              Le Club Francophone
            </Link>

            {/* Mobile controls (visible <lg) */}
            <div className="flex lg:hidden items-center gap-2">
              <LanguageToggle variant="light" />
              <CurrencySelector variant="light" />
              <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Open menu"
                aria-expanded={open}
                data-testid="nav-hamburger"
                className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-[#1A2F4A] text-white hover:bg-[#243a58] transition-colors focus:outline-none focus:ring-2 focus:ring-[#D4AF37]">
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-[100]" data-testid="mobile-drawer" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} aria-hidden="true" />
          <aside
            className="absolute top-0 right-0 h-full w-[86%] max-w-sm bg-[#FAF7F0] shadow-2xl flex flex-col overflow-y-auto"
            style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E4DC]">
              <span className="text-lg font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>Menu</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-drawer-close"
                className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-[#E8E4DC] hover:border-[#D4AF37] text-[#1A2F4A] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Main links */}
            <nav className="flex-1 px-3 py-3">
              {mainLinks.map((l) => (
                <Link
                  key={l.testid}
                  to={l.to}
                  data-testid={`drawer-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className="block px-4 py-3 min-h-[44px] text-[15px] font-semibold text-[#1A2F4A] rounded-xl hover:bg-white hover:text-[#D4AF37] transition-colors">
                  {typeof l.label === 'string' ? l.label : t(l.label)}
                </Link>
              ))}

              <div className="my-3 h-px bg-[#E8E4DC]" />

              <Link
                to="/club-francophone"
                onClick={() => setOpen(false)}
                data-testid="drawer-club-francophone"
                className="flex items-center gap-2 mx-1 my-2 px-4 py-3 min-h-[44px] bg-[#D4AF37] hover:bg-[#C9A961] text-[#1A2F4A] rounded-full text-sm font-bold justify-center shadow-md">
                <MessageCircle className="w-4 h-4" />
                Le Club Francophone
              </Link>

              <div className="my-3 h-px bg-[#E8E4DC]" />

              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    data-testid="drawer-mon-espace"
                    className="flex items-center gap-2 px-4 py-3 min-h-[44px] text-[14px] font-semibold text-[#1A2F4A] hover:bg-white rounded-xl">
                    <User className="w-4 h-4" />
                    {t({ fr: 'Espace étudiant', en: 'Student Portal' })}
                  </Link>
                  <button
                    type="button"
                    onClick={() => { logout(); setOpen(false); }}
                    data-testid="drawer-logout"
                    className="w-full flex items-center gap-2 px-4 py-3 min-h-[44px] text-[14px] font-semibold text-[#1A2F4A] hover:bg-white rounded-xl">
                    <LogOut className="w-4 h-4" />
                    {t({ fr: 'Se déconnecter', en: 'Log out' })}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    data-testid="drawer-login"
                    className="flex items-center gap-2 px-4 py-3 min-h-[44px] text-[14px] font-semibold text-[#1A2F4A] hover:bg-white rounded-xl">
                    <User className="w-4 h-4" />
                    {t({ fr: 'Se connecter', en: 'Sign In' })}
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setOpen(false)}
                    data-testid="drawer-signup"
                    className="flex items-center gap-2 justify-center mx-1 my-2 px-4 py-3 min-h-[44px] bg-[#1A2F4A] hover:bg-[#243a58] text-white rounded-full text-sm font-bold">
                    {t({ fr: 'Inscription gratuite', en: 'Start for Free' })}
                  </Link>
                </>
              )}

              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="drawer-contact"
                className="flex items-center gap-2 px-4 py-3 min-h-[44px] text-[14px] font-semibold text-[#1A2F4A] hover:bg-white rounded-xl">
                <Mail className="w-4 h-4" />
                {t({ fr: 'Nous contacter', en: 'Contact Us' })}
              </Link>
            </nav>
          </aside>
        </div>
      )}
    </>
  );
};

export default NavigationFinal;
